import NumericValue from '@/components/NumericValue';
import { Button } from '@/components/shadcn/button';
import { Progress } from '@/components/shadcn/progress';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/shadcn/table';
import { Textarea } from '@/components/shadcn/textarea';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/shadcn/tooltip';
import { isTestEnv } from '@/constant';
import { getAgentApi } from '@/gql';
import { useAgentExtraInfo } from '@/hooks/useAgentExtraInfo';
import { useAgentInfo } from '@/hooks/useAgentInfo';
import { useChatLimit } from '@/hooks/useChatLimit';
import { useKnowledges } from '@/hooks/useKnowledges';
import { useTokenDetail } from '@/hooks/useTokenDetail';
import Alert from '@/utils/components/alert';
import InfoIcon from '@mui/icons-material/Info';
import { CircularProgress } from '@mui/material';
import { BigNumber } from 'bignumber.js';
import makeBlockie from 'ethereum-blockies-base64';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import numeral from 'numeral';
import { FC, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Chat from './Chat';

const Agent: FC = observer(() => {
    const { agentId: agentIdParam }: { agentId: string } = useParams();
    const history = useHistory();
    const [activeTab, setActiveTab] = useState(0);
    const [value, setValue] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const agentId = agentIdParam?.toLowerCase() || '';

    const { agentInfo, loading: agentLoading } = useAgentInfo(agentId);
    const { tokenDetail, loading: tokenDetailLoading } =
        useTokenDetail(agentId);
    const {
        knowledges,
        loading: knowledgesLoading,
        refetch: refetchKnowledges,
    } = useKnowledges(agentId);
    const {
        chatLimit,
        chatCount,
        refetch: refetchChatLimit,
    } = useChatLimit(agentId);

    const {
        trendingIndex,
        coBuildPointsByUser,
        countUserChatsToday,
        countUserSubmittedKnowledgesToday,
        refetch: refetchAgentExtraInfo,
    } = useAgentExtraInfo(agentId);

    const progressValue = useMemo(() => {
        if (!tokenDetail?.collateral || !tokenDetail?.fundingGoal) return 0;
        return BigNumber(tokenDetail.collateral)
            .dividedBy(tokenDetail.fundingGoal)
            .multipliedBy(100)
            .toNumber();
    }, [tokenDetail]);

    const launchPriceChange = useMemo(() => {
        if (!tokenDetail?.initialPrice) {
            return 0;
        }

        return BigNumber(tokenDetail.nowPrice ?? 0)
            .minus(tokenDetail.initialPrice)
            .dividedBy(tokenDetail.initialPrice)
            .toNumber();
    }, [tokenDetail]);

    const submitKnowledge = async () => {
        if (countUserSubmittedKnowledgesToday >= 10) {
            Alert({
                type: 'error',
                text: 'You have reached the maximum number of submissions for today.',
            });
            return;
        }
        if (submitting) return;
        try {
            setSubmitting(true);
            const api = getAgentApi();
            await api.submitKnowledge({
                agentUid: agentId,
                knowledge: {
                    text: value,
                },
            });
            Alert({
                type: 'success',
                text: 'Submitted successfully!',
            });
            setValue('');
            refetchAgentExtraInfo();
            refetchKnowledges();
        } catch (error: any) {
            Alert({
                type: 'error',
                text:
                    error?.response?.errors?.[0]?.message ||
                    'Failed to submit!',
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="px-[40px] max-md:px-[16px] h-[calc(100vh-80px)] py-[40px] max-md:py-[20px] flex flex-col overflow-auto">
            {agentLoading ? (
                <CircularProgress className="mt-[60px] mx-auto" thickness={6} />
            ) : agentInfo ? (
                <>
                    <div className="flex mb-[40px] max-md:mb-[24px] max-md:flex-col max-md:items-center max-md:gap-[20px] items-center gap-[10px]">
                        <div className="w-[80px] h-[80px] rounded-full overflow-hidden">
                            <img
                                src={tokenDetail?.imgUrl}
                                alt="logo"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="max-md:w-full">
                            <div className="flex items-center gap-[10px] max-md:justify-center">
                                <div className="font-medium text-[20px] max-md:text-[18px]">
                                    {agentInfo.name}
                                </div>
                                <div className="bg-success/10 text-success text-[12px] px-[6px] py-[2px] rounded-[8px]">
                                    Play&Fun
                                </div>
                            </div>
                            <div className="text-[12px] my-[10px] max-md:text-center">
                                {agentInfo.tagline}
                            </div>
                            <div className="flex items-center text-[12px] gap-[4px] max-md:justify-center">
                                <img
                                    src={
                                        tokenDetail?.creatorObj?.headImgUrl ||
                                        tokenDetail?.creatorObj?.address
                                            ? makeBlockie(
                                                  tokenDetail?.creatorObj
                                                      ?.address ?? '',
                                              )
                                            : undefined
                                    }
                                    alt="avatar"
                                    className="w-[16px] h-[16px] rounded-full"
                                />
                                <span
                                    className="hover:underline cursor-pointer hover:text-primary"
                                    onClick={() =>
                                        window.open(
                                            `https://${
                                                isTestEnv ? 'test.' : ''
                                            }lazpad.fun/profile/${
                                                tokenDetail?.creatorObj?.address
                                            }`,
                                            '_blank',
                                        )
                                    }
                                >
                                    {tokenDetail?.creatorObj?.nickName}
                                </span>
                            </div>
                        </div>
                        <div className="w-[146px] ml-auto h-[67px] flex flex-col justify-between items-center bg-background rounded-[4px] p-[10px] shrink-0 max-md:w-full max-md:items-center">
                            <div className="text-[18px] font-bold truncate w-full text-center max-md:text-left max-md:w-auto">
                                {trendingIndex}
                            </div>
                            <div className="text-gray text-[12px] max-md:text-right flex items-center gap-[4px]">
                                <div>Trending Index</div>
                                <TooltipProvider delayDuration={0}>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <InfoIcon className="w-[14px] h-[14px]" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>
                                                Agent’s popularity is measured
                                                daily using a combination of
                                                usage and trading volume.
                                            </p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                        </div>
                        <div className="w-[146px] h-[67px] flex flex-col justify-between items-center bg-background rounded-[4px] p-[10px] shrink-0 max-md:w-full max-md:items-center">
                            <div className="text-[18px] font-bold truncate w-full text-center max-md:text-left max-md:w-auto">
                                {countUserChatsToday}/{agentInfo.dailyChatLimit}
                            </div>
                            <div className="text-gray text-[12px] max-md:text-right flex items-center gap-[4px]">
                                <div>Test Messages</div>
                                <TooltipProvider delayDuration={0}>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <InfoIcon className="w-[14px] h-[14px]" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>
                                                Each agent has a shared daily
                                                message quota based on its
                                                trending index, updated daily at
                                                00:00 UTC.
                                            </p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                        </div>
                        <div className="w-[146px] h-[67px] flex flex-col justify-between items-center bg-background rounded-[4px] p-[10px] shrink-0 max-md:w-full max-md:items-center">
                            <div className="text-[18px] font-bold truncate w-full text-center max-md:text-left max-md:w-auto">
                                {coBuildPointsByUser}
                            </div>
                            <div className="text-gray text-[12px] max-md:text-right">
                                Your Co-build Points
                            </div>
                        </div>
                        <div className="hidden border border-border rounded-[16px] p-[20px] w-full mt-auto max-md:block">
                            <div className="flex items-center gap-[10px]">
                                <div className="font-semibold">
                                    {tokenDetail?.name}
                                </div>
                                <div className="text-[12px]">
                                    {tokenDetail?.symbol}
                                </div>
                            </div>
                            <div className="mt-[20px]">
                                <NumericValue
                                    value={launchPriceChange}
                                    showPercentage
                                    showSign
                                />
                            </div>
                            <div className="flex mt-[20px] text-[14px] items-center justify-between">
                                <div className="text-gray">Market Cap</div>
                                <div>
                                    $
                                    {numeral(
                                        BigNumber(
                                            tokenDetail?.totalPriceUsd ?? 0,
                                        ).toFixed(),
                                    ).format('0.00a')}
                                </div>
                            </div>
                            <div className="flex text-[14px] items-center gap-[10px] mt-[20px]">
                                <Progress
                                    value={progressValue}
                                    className="flex-1"
                                />
                                <div>{progressValue.toFixed(2)}%</div>
                            </div>
                            <Button
                                variant="outline"
                                className="w-full mt-[20px] text-[14px]"
                                onClick={() => {
                                    window.open(
                                        `https://${
                                            isTestEnv ? 'test.' : ''
                                        }lazpad.fun/token/${agentId}`,
                                        '_blank',
                                    );
                                }}
                            >
                                Go to Token Page
                            </Button>
                        </div>
                    </div>
                    <div className="flex gap-[40px] max-md:gap-[20px] flex-1 max-md:flex-col">
                        <div className="w-[200px] flex flex-col gap-[20px] max-md:w-full max-md:flex-row max-md:gap-[12px]">
                            <div
                                className={`font-medium px-[20px] rounded-[16px] cursor-pointer h-[60px] flex items-center max-md:text-[12px] ${
                                    activeTab === 0
                                        ? 'text-primary bg-primary/10'
                                        : ''
                                } max-md:h-auto max-md:py-[12px] max-md:px-[16px]`}
                                onClick={() => setActiveTab(0)}
                            >
                                Agent Test
                            </div>
                            <div
                                className={`font-medium px-[20px] rounded-[16px] cursor-pointer h-[60px] flex items-center max-md:text-[12px] ${
                                    activeTab === 1
                                        ? 'text-primary bg-primary/10'
                                        : ''
                                } max-md:h-auto max-md:py-[12px] max-md:px-[16px]`}
                                onClick={() => setActiveTab(1)}
                            >
                                Co-build Agent
                            </div>
                            <div
                                className={`font-medium px-[20px] rounded-[16px] cursor-pointer h-[60px] flex items-center max-md:text-[12px] ${
                                    activeTab === 2
                                        ? 'text-primary bg-primary/10'
                                        : ''
                                } max-md:h-auto max-md:py-[12px] max-md:px-[16px]`}
                                onClick={() => setActiveTab(2)}
                            >
                                History
                            </div>
                            <div className="border border-border rounded-[16px] p-[20px] mt-auto max-md:hidden">
                                <div className="flex items-center gap-[10px]">
                                    <div className="font-semibold">
                                        {tokenDetail?.name}
                                    </div>
                                    <div className="text-[12px]">
                                        {tokenDetail?.symbol}
                                    </div>
                                </div>
                                <div className="mt-[20px]">
                                    <NumericValue
                                        value={launchPriceChange}
                                        showPercentage
                                        showSign
                                    />
                                </div>
                                <div className="flex mt-[20px] text-[14px] items-center justify-between">
                                    <div className="text-gray">Market Cap</div>
                                    <div>
                                        $
                                        {numeral(
                                            BigNumber(
                                                tokenDetail?.totalPriceUsd ?? 0,
                                            ).toFixed(),
                                        ).format('0.00a')}
                                    </div>
                                </div>
                                <div className="flex text-[14px] items-center gap-[10px] mt-[20px]">
                                    <Progress
                                        value={progressValue}
                                        className="flex-1"
                                    />
                                    <div>{progressValue.toFixed(2)}%</div>
                                </div>
                                <Button
                                    variant="outline"
                                    className="w-full mt-[20px] text-[14px]"
                                    onClick={() => {
                                        window.open(
                                            `https://${
                                                isTestEnv ? 'test.' : ''
                                            }lazpad.fun/token/${agentId}`,
                                            '_blank',
                                        );
                                    }}
                                >
                                    Go to Token Page
                                </Button>
                            </div>
                        </div>
                        <div className="bg-background rounded-[8px] p-[20px] flex-1 relative max-md:w-full max-md:p-[16px]">
                            {activeTab === 0 && (
                                <Chat
                                    agent={agentInfo}
                                    tokenDetail={tokenDetail}
                                    chatLimit={chatLimit}
                                    totalChatLimit={agentInfo.dailyChatLimit}
                                    totalChatCount={countUserChatsToday}
                                    chatCount={chatCount}
                                    refetchChatLimit={refetchChatLimit}
                                    refetchAgentExtraInfo={
                                        refetchAgentExtraInfo
                                    }
                                />
                            )}
                            {activeTab === 1 && (
                                <div>
                                    <div className="font-semibold">
                                        <span className="text-error">*</span>
                                        Contribute data
                                    </div>
                                    <div className="relative">
                                        <Textarea
                                            placeholder="Input your data here..."
                                            className="mt-[10px] pb-[60px]"
                                            maxLength={5000}
                                            rows={10}
                                            onChange={(e) =>
                                                setValue(e.target.value)
                                            }
                                            value={value}
                                        />
                                        <div className="absolute bottom-[20px] right-[20px] text-[12px] text-gray">
                                            {value.length || 0}/5000
                                        </div>
                                    </div>
                                    <Button
                                        className="text-[14px] mt-[27px] px-[20px] py-[10px]"
                                        onClick={submitKnowledge}
                                        disabled={submitting}
                                    >
                                        {submitting
                                            ? 'Submitting...'
                                            : 'Submit'}
                                    </Button>
                                    <div className="bg-border rounded-[8px] p-[10px] mt-[27px] text-[12px] text-gray">
                                        For each data contribution, you will
                                        earn 5 points. You can contribute up to
                                        10 times per day. If your data is
                                        relevant to the user’s question, you
                                        will receive bonus points.
                                    </div>
                                </div>
                            )}
                            {activeTab === 2 && (
                                <div>
                                    <div className="font-semibold">History</div>
                                    <Table className="text-[12px] mt-[10px]">
                                        <TableHeader className="bg-border">
                                            <TableRow>
                                                <TableHead className="rounded-tl-[8px] w-1/2 px-[20px]">
                                                    Knowledge
                                                </TableHead>
                                                <TableHead className="rounded-tr-[8px] w-1/2 px-[20px]">
                                                    Submitted date
                                                </TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody className="bg-white">
                                            {knowledges.length > 0 ? (
                                                knowledges.map((knowledge) => (
                                                    <TableRow
                                                        key={knowledge.id}
                                                    >
                                                        <TableCell className="px-[20px]">
                                                            {knowledge.text}
                                                        </TableCell>
                                                        <TableCell className="px-[20px]">
                                                            {moment(
                                                                knowledge.createdAt,
                                                            ).format(
                                                                'MMM DD, YYYY',
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={2}
                                                        className="text-center"
                                                    >
                                                        No data
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            ) : (
                <div className="mt-[100px] flex flex-col items-center gap-[20px]">
                    <p className="text-[14px] text-center text-gray-600">
                        Something went wrong. Please refresh the page to try
                        again.
                    </p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-[20px] py-[10px] bg-primary text-white rounded-[8px] text-[14px] hover:bg-primary/80 transition-colors"
                    >
                        Refresh Page
                    </button>
                </div>
            )}
        </div>
    );
});

export default Agent;
